import React from 'react';

import {
    comboEnumLabel,
    comboShapeTagLabel,
    outcomeLabel,
} from '../../../data/deckComboProfileMarkers';

/**
 * Combo profile panel — the collapsible "what the combo package is doing"
 * block under the combos-pane hero. Bundles four layers so callers can
 * render them together:
 *   1. Headline (one-sentence elevator pitch)
 *   2. Strengths / Risks / Notable marker chip groups
 *   3. Top combos (from `profile.highlights`)
 *   4. Mechanical facets — composition, outcomes, speed, friction,
 *      payoff, score-weight — using the shared `.powerFacet*` styles.
 *
 * Everything reads from `deck.details.combos.profile`. The panel returns
 * `null` when there is no combo content so the parent pane can skip the
 * whole collapsible shell.
 */

const DENSITY_LABEL = { light: 'Light', moderate: 'Moderate', dense: 'Dense' };

function DensityPill({ density }) {
    if (!density || density === 'none') return null;
    return (
        <span className={`powerFacetDensity powerFacetDensity--${density}`}>
            {DENSITY_LABEL[density]}
        </span>
    );
}

function FacetHeader({ title, density, headline }) {
    return (
        <div className="powerFacetHeader">
            <div className="powerFacetTitleRow">
                <span className="powerFacetTitle">{title}</span>
                <DensityPill density={density} />
            </div>
            {headline && <span className="powerFacetHeadline">{headline}</span>}
        </div>
    );
}

function StatRow({ stats }) {
    const visible = (stats || []).filter((s) => s && s.value != null && s.value !== '');
    if (visible.length === 0) return null;
    return (
        <div className="powerFacetStats">
            {visible.map((s, i) => (
                <span key={i} className="powerFacetStat" title={s.hint || undefined}>
                    <strong>{s.value}</strong> {s.label}
                </span>
            ))}
        </div>
    );
}

function Badges({ items }) {
    const visible = (items || []).filter(Boolean);
    if (visible.length === 0) return null;
    return (
        <div className="powerFacetBadges">
            {visible.map((b, i) => (
                <span key={i} className={`powerFacetBadge powerFacetBadge--${b.tone || 'warn'}`}>
                    {b.label}
                </span>
            ))}
        </div>
    );
}

function BigNumber({ value, label }) {
    if (value == null) return null;
    return (
        <div className="powerFacetBigNumber">
            <span className="powerFacetBigValue">{value}</span>
            <span className="powerFacetBigLabel">{label}</span>
        </div>
    );
}

function formatPct(n) {
    if (n == null || Number.isNaN(Number(n))) return null;
    return `${Math.round(Number(n) * 100)}%`;
}

function formatDec(n, d = 1) {
    if (n == null || Number.isNaN(Number(n))) return null;
    return Number(n).toFixed(d);
}

// ---- Markers ---------------------------------------------------------

function MarkerGroup({ label, tone, chips }) {
    if (!chips || chips.length === 0) return null;
    return (
        <div className={`combosMarkerGroup combosMarkerGroup--${tone}`}>
            <span className="combosMarkerGroupLabel">{label}</span>
            <div className="combosMarkerChipRow">
                {chips.map((chip) => (
                    <span
                        key={chip.key}
                        className={`powerPatternChip powerPatternChip--${chip.tone}`}
                        title={chip.detail || undefined}
                    >
                        {chip.label}
                    </span>
                ))}
            </div>
        </div>
    );
}

function MarkersSection({ headline, strengths, risks, notable }) {
    const hasAny =
        (strengths && strengths.length > 0) ||
        (risks && risks.length > 0) ||
        (notable && notable.length > 0);
    if (!headline && !hasAny) return null;
    return (
        <div className="combosProfileMarkers">
            {headline && <p className="combosHeadline">{headline}</p>}
            {hasAny && (
                <div className="combosMarkerGroups">
                    <MarkerGroup label="Strengths" tone="alert" chips={strengths} />
                    <MarkerGroup label="Risks" tone="warn" chips={risks} />
                    <MarkerGroup label="Notable" tone="cool" chips={notable} />
                </div>
            )}
        </div>
    );
}

// ---- Top combos (highlights) ----------------------------------------

function HighlightCardChip({ card, onSelectCard }) {
    if (!card) return null;
    const name = card.name || card.id || 'Unknown';
    const handleClick = (e) => {
        e.stopPropagation();
        onSelectCard?.(card);
    };
    return (
        <span
            className="combosHighlightCardChip"
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick(e);
                }
            }}
            title={`View ${name}`}
        >
            {card.imageUri ? (
                <img src={card.imageUri} alt={name} className="combosHighlightCardThumb" loading="lazy" />
            ) : (
                <span className="combosHighlightCardThumb combosHighlightCardThumb--empty" />
            )}
            <span className="combosHighlightCardName">{name}</span>
        </span>
    );
}

function HighlightRow({ highlight, onSelectCard }) {
    const badges = [
        highlight.isWincon && { tone: 'alert', label: 'Wincon' },
        highlight.isTwoCard && { tone: 'alert', label: '2-card' },
        highlight.isClean && { tone: 'ok', label: 'Clean' },
        highlight.isManaSink && { tone: 'gold', label: 'Mana sink' },
    ].filter(Boolean);

    return (
        <div className="combosHighlightRow">
            <div className="combosHighlightCards">
                {highlight.__cards.map((card, idx) => (
                    <HighlightCardChip
                        key={`${highlight.comboId || 'h'}_${card?.id || idx}`}
                        card={card}
                        onSelectCard={onSelectCard}
                    />
                ))}
            </div>
            <div className="combosHighlightMeta">
                <div className="combosHighlightMetaTop">
                    <span className="combosHighlightOutcome">
                        {outcomeLabel(highlight.outcome)}
                    </span>
                    {badges.map((b) => (
                        <span key={b.label} className={`comboFlag comboFlag--${b.tone}`}>
                            {b.label}
                        </span>
                    ))}
                </div>
                <div className="combosHighlightMetaBottom">
                    <span className="combosHighlightStat">
                        <strong>{Math.round(highlight.score || 0)}</strong> score
                    </span>
                    <span className="combosHighlightStat">
                        <strong>{highlight.manaCost}</strong> mana
                    </span>
                    <span className="combosHighlightStat">
                        <strong>{highlight.pieceCount}</strong>{' '}
                        piece{highlight.pieceCount === 1 ? '' : 's'}
                    </span>
                </div>
            </div>
        </div>
    );
}

function HighlightsSection({ highlights, onSelectCard }) {
    if (!highlights || highlights.length === 0) return null;
    return (
        <div className="combosProfileSubsection">
            <header className="combosProfileSubsectionHeader">
                <span className="combosProfileSubsectionTitle">Top combos</span>
                <span className="combosProfileSubsectionHint">
                    The highest-scoring combos in the deck — ordered by impact.
                </span>
            </header>
            <div className="combosHighlightsList">
                {highlights.map((h, i) => (
                    <HighlightRow
                        key={h.comboId || `highlight_${i}`}
                        highlight={h}
                        onSelectCard={onSelectCard}
                    />
                ))}
            </div>
        </div>
    );
}

// ---- Facets ---------------------------------------------------------

function CompositionFacet({ composition, lockCount }) {
    if (!composition) return null;
    const pieces = composition.cardPieceCounts || {};
    const unique = composition.uniqueComboCards || 0;
    const share = composition.comboCardShare;
    if (!unique && !pieces.two && !pieces.three && !pieces.fourPlus) return null;
    const stats = [
        pieces.two ? { value: pieces.two, label: '2-card' } : null,
        pieces.three ? { value: pieces.three, label: '3-card' } : null,
        pieces.fourPlus ? { value: pieces.fourPlus, label: '4+ card' } : null,
        lockCount ? { value: lockCount, label: 'lock' } : null,
    ];
    const badges = [
        composition.manaSinkCount > 0 && {
            tone: 'warn',
            label: `${composition.manaSinkCount} mana sink${composition.manaSinkCount === 1 ? '' : 's'}`,
        },
        composition.templateMatchedCount > 0 && {
            tone: 'cool',
            label: `${composition.templateMatchedCount} template match${composition.templateMatchedCount === 1 ? '' : 'es'}`,
        },
        composition.commanderAnchoredCount > 0 && {
            tone: 'warn',
            label: `${composition.commanderAnchoredCount} commander-anchored`,
        },
    ];
    const headline =
        share != null
            ? `${formatPct(share)} of nonland cards touch a combo`
            : null;
    return (
        <div className="powerFacet">
            <FacetHeader title="Composition" headline={headline} />
            <BigNumber value={unique || null} label="combo cards" />
            <StatRow stats={stats} />
            <Badges items={badges} />
        </div>
    );
}

function OutcomesFacet({ outcomes, primaryOutcome, distinctWinconOutcomes }) {
    if (!outcomes) return null;
    const entries = Object.entries(outcomes)
        .map(([key, entry]) => ({ key, ...(entry || {}) }))
        .filter((e) => (e.count || 0) > 0)
        .sort((a, b) => (b.count || 0) - (a.count || 0));
    if (entries.length === 0) return null;

    const total = entries.reduce((sum, e) => sum + (e.count || 0), 0);
    const winconPaths = Array.isArray(distinctWinconOutcomes) ? distinctWinconOutcomes : [];
    const headlineBits = [];
    if (primaryOutcome) headlineBits.push(`Leans ${outcomeLabel(primaryOutcome).toLowerCase()}`);
    if (winconPaths.length >= 2) headlineBits.push(`${winconPaths.length} wincon paths`);
    const headline = headlineBits.length ? headlineBits.join(' · ') : null;

    return (
        <div className="powerFacet powerFacet--wide">
            <FacetHeader title="Outcomes" headline={headline} />
            <div className="comboOutcomeGrid">
                {entries.map((entry) => {
                    const pct = total > 0 ? Math.round((entry.count / total) * 100) : 0;
                    const isWincon = !!entry.isWinconBucket;
                    const isPrimary = entry.key === primaryOutcome;
                    return (
                        <div
                            key={entry.key}
                            className={`comboOutcomePill${
                                isWincon ? ' comboOutcomePill--wincon' : ''
                            }${isPrimary ? ' comboOutcomePill--primary' : ''}`}
                            title={`${entry.count} combo${entry.count === 1 ? '' : 's'} · ${pct}% of outcome hits`}
                        >
                            <span className="comboOutcomePillLabel">
                                {isWincon && <span className="comboOutcomePillIcon" aria-hidden="true">★</span>}
                                {outcomeLabel(entry.key)}
                            </span>
                            <span className="comboOutcomePillCount">×{entry.count}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function SpeedFacet({ speed }) {
    if (!speed) return null;
    const buckets = speed.manaCostBuckets || {};
    const anyBucket =
        (buckets.upToThree || 0) +
            (buckets.fourToFive || 0) +
            (buckets.sixToEight || 0) +
            (buckets.ninePlus || 0) >
        0;
    if (!speed.averageBias && !speed.minWinconManaCost && !speed.cleanLineCount && !anyBucket) {
        return null;
    }

    const stats = [
        speed.minWinconManaCost
            ? { value: speed.minWinconManaCost, label: 'min wincon mana' }
            : null,
        speed.averageBias != null
            ? {
                  value: formatDec(speed.averageBias, 2),
                  label: 'avg bias',
                  hint: 'Bias 1.0 = cleanest / most deterministic.',
              }
            : null,
        speed.averageAdditionalMana > 0
            ? { value: `+${formatDec(speed.averageAdditionalMana, 1)}`, label: 'extra mana' }
            : null,
        speed.cleanLineCount
            ? { value: speed.cleanLineCount, label: 'clean line' }
            : null,
    ];

    const barRows = [
        { key: 'upToThree', label: '≤ 3 mana', count: buckets.upToThree || 0 },
        { key: 'fourToFive', label: '4–5', count: buckets.fourToFive || 0 },
        { key: 'sixToEight', label: '6–8', count: buckets.sixToEight || 0 },
        { key: 'ninePlus', label: '9+', count: buckets.ninePlus || 0 },
    ];
    const maxBucket = barRows.reduce((m, r) => Math.max(m, r.count), 0);

    // Self-contained phrase ("Quick to deploy"), not "<enum> deployment"
    // — deploymentBand reuses the density enum, so the old form produced
    // "Dense deployment", which says nothing about speed.
    const headline = comboShapeTagLabel('deploymentBand', speed.deploymentBand);

    return (
        <div className="powerFacet powerFacet--wide">
            <FacetHeader title="Speed" density={speed.cleanLineDensity} headline={headline} />
            <StatRow stats={stats} />
            {maxBucket > 0 && (
                <div className="powerFacetTypeBars">
                    {barRows
                        .filter((r) => r.count > 0)
                        .map((r) => {
                            const w = maxBucket > 0 ? (r.count / maxBucket) * 100 : 0;
                            return (
                                <div key={r.key} className="powerFacetTypeRow">
                                    <span className="powerFacetTypeLabel">{r.label}</span>
                                    <div className="powerFacetTypeBarTrack">
                                        <div
                                            className="powerFacetTypeBarFill"
                                            style={{ width: `${w}%` }}
                                        />
                                    </div>
                                    <span className="powerFacetTypeValue">{r.count}</span>
                                </div>
                            );
                        })}
                </div>
            )}
            <span className="powerFacetSubNote">total-to-deploy buckets (CMCs + additional mana)</span>
        </div>
    );
}

const QUALIFIER_LABELS = {
    requiresActivationCapable: 'Summoning-sick creature',
    requiresNoAttack: 'Cannot have attacked',
    requiresPayLife: 'Pay life',
    requiresPlayLand: 'Play-a-land',
    requiresCardInHand: 'Specific card in hand',
    requiresSacrificeAnotherCreature: 'Sacrifice fodder',
    requiresPayMana: 'Pay mana per loop',
    hasExceptionClause: 'Exception clause',
    hasBeginningOfEndStepTrigger: 'End-step trigger',
};

function FrictionFacet({ friction }) {
    if (!friction) return null;
    const qualifiers = friction.qualifiers || {};
    const activeQualifiers = Object.entries(qualifiers)
        .filter(([, n]) => Number(n) > 0)
        .sort((a, b) => b[1] - a[1]);
    if (
        activeQualifiers.length === 0 &&
        !friction.boardDependentCount &&
        !friction.keywordDependentCount
    ) {
        return null;
    }

    const stats = [
        friction.boardDependentCount
            ? { value: friction.boardDependentCount, label: 'board-dependent' }
            : null,
        friction.keywordDependentCount
            ? { value: friction.keywordDependentCount, label: 'keyword-gated' }
            : null,
        friction.averageQualifiersPerCombo != null
            ? {
                  value: formatDec(friction.averageQualifiersPerCombo, 1),
                  label: 'qual / combo',
              }
            : null,
    ];
    const badges = activeQualifiers.slice(0, 6).map(([key, count]) => ({
        tone: 'warn',
        label: `${QUALIFIER_LABELS[key] || key} ×${count}`,
    }));

    return (
        <div className="powerFacet">
            <FacetHeader title="Friction" density={friction.friction} />
            <StatRow stats={stats} />
            <Badges items={badges} />
        </div>
    );
}

function PayoffFacet({ payoff, cards, onSelectCard }) {
    if (!payoff) return null;
    const isolationLabel = comboShapeTagLabel('winconLineIsolation', payoff.winconLineIsolation);
    const stats = [
        payoff.enablersFeedingWincons
            ? { value: payoff.enablersFeedingWincons, label: 'enablers feed wincons' }
            : null,
        payoff.versatilePieceCount
            ? { value: payoff.versatilePieceCount, label: 'versatile pieces' }
            : null,
        payoff.avgCombosPerCard != null && payoff.avgCombosPerCard > 0
            ? { value: formatDec(payoff.avgCombosPerCard, 1), label: 'avg combos / card' }
            : null,
    ];

    const anchorCard = payoff.primaryAnchorCardId ? cards?.[payoff.primaryAnchorCardId] : null;
    const anchorCount = payoff.primaryAnchorComboCount || 0;
    const badges = [
        payoff.singleCardAnchor && {
            tone: 'warn',
            label: 'Single-card anchor',
        },
    ];

    return (
        <div className="powerFacet">
            <FacetHeader title="Payoff" headline={isolationLabel} />
            <StatRow stats={stats} />
            {anchorCard && (
                <div
                    className="comboAnchorRow"
                    onClick={() => onSelectCard?.(anchorCard)}
                    role={onSelectCard ? 'button' : undefined}
                    tabIndex={onSelectCard ? 0 : undefined}
                    onKeyDown={(e) => {
                        if (!onSelectCard) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelectCard(anchorCard);
                        }
                    }}
                    title={`${anchorCard.name} appears in ${anchorCount} combo${anchorCount === 1 ? '' : 's'}`}
                >
                    {anchorCard.imageUri ? (
                        <img
                            src={anchorCard.imageUri}
                            alt={anchorCard.name}
                            className="comboAnchorThumb"
                            loading="lazy"
                        />
                    ) : (
                        <span className="comboAnchorThumb comboAnchorThumb--empty" />
                    )}
                    <div className="comboAnchorMeta">
                        <span className="comboAnchorLabel">Primary anchor</span>
                        <span className="comboAnchorName">{anchorCard.name || payoff.primaryAnchorCardId}</span>
                        <span className="comboAnchorCount">
                            {anchorCount} combo{anchorCount === 1 ? '' : 's'}
                        </span>
                    </div>
                </div>
            )}
            <Badges items={badges} />
        </div>
    );
}

function ScoreContributionFacet({ scoreContribution, cards, onSelectCard }) {
    if (!scoreContribution) return null;
    const { totalScore, averagePerCombo, topComboShare, topComboId } = scoreContribution;
    if (!totalScore) return null;

    const stats = [
        averagePerCombo
            ? { value: Math.round(averagePerCombo), label: 'avg / combo' }
            : null,
        topComboShare > 0
            ? {
                  value: formatPct(topComboShare),
                  label: 'top combo',
                  hint: 'Share of combo score carried by the highest-scoring combo.',
              }
            : null,
    ];

    const winconMagLabel = comboEnumLabel('magnitude', scoreContribution.magnitudeFromWincons);
    const enablerMagLabel = comboEnumLabel('magnitude', scoreContribution.magnitudeFromEnablers);
    const badges = [
        winconMagLabel && winconMagLabel !== 'None' && {
            tone: 'alert',
            label: `${winconMagLabel} from wincons`,
        },
        enablerMagLabel && enablerMagLabel !== 'None' && {
            tone: 'warn',
            label: `${enablerMagLabel} from enablers`,
        },
    ];

    const topCard = topComboId ? resolveTopComboCard(topComboId, cards) : null;

    return (
        <div className="powerFacet">
            <FacetHeader title="Score weight" />
            <BigNumber value={Math.round(totalScore)} label="combo score" />
            <StatRow stats={stats} />
            {topCard && (
                <div
                    className="comboAnchorRow"
                    onClick={() => onSelectCard?.(topCard)}
                    role={onSelectCard ? 'button' : undefined}
                    tabIndex={onSelectCard ? 0 : undefined}
                    onKeyDown={(e) => {
                        if (!onSelectCard) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelectCard(topCard);
                        }
                    }}
                    title={`${topCard.name} drives the top-scoring combo`}
                >
                    {topCard.imageUri ? (
                        <img
                            src={topCard.imageUri}
                            alt={topCard.name}
                            className="comboAnchorThumb"
                            loading="lazy"
                        />
                    ) : (
                        <span className="comboAnchorThumb comboAnchorThumb--empty" />
                    )}
                    <div className="comboAnchorMeta">
                        <span className="comboAnchorLabel">Top combo</span>
                        <span className="comboAnchorName">{topCard.name}</span>
                    </div>
                </div>
            )}
            <Badges items={badges} />
        </div>
    );
}

// Top-combo ids are underscore-joined card ids. Resolve the first card id
// that exists in the cards map so we can render a thumbnail. If nothing
// resolves, return null and the facet skips the anchor row.
function resolveTopComboCard(topComboId, cards) {
    if (!topComboId || !cards) return null;
    const parts = topComboId.split('_');
    for (let size = parts.length; size >= 1; size -= 1) {
        for (let start = 0; start + size <= parts.length; start += 1) {
            const candidate = parts.slice(start, start + size).join('_');
            if (cards[candidate]) return cards[candidate];
        }
    }
    return null;
}

function FacetsSection({ profile, cards, onSelectCard }) {
    return (
        <div className="combosProfileSubsection">
            <header className="combosProfileSubsectionHeader">
                <span className="combosProfileSubsectionTitle">Mechanical shape</span>
                <span className="combosProfileSubsectionHint">
                    Piece counts, outcomes, deployment speed, friction, and which cards drive the score.
                </span>
            </header>
            <div className="powerFacetGrid">
                <CompositionFacet composition={profile.composition} lockCount={profile.lockCount} />
                <OutcomesFacet
                    outcomes={profile.outcomes}
                    primaryOutcome={profile.primaryOutcome}
                    distinctWinconOutcomes={profile.distinctWinconOutcomes}
                />
                <SpeedFacet speed={profile.speed} />
                <FrictionFacet friction={profile.friction} />
                <PayoffFacet payoff={profile.payoff} cards={cards} onSelectCard={onSelectCard} />
                <ScoreContributionFacet
                    scoreContribution={profile.scoreContribution}
                    cards={cards}
                    onSelectCard={onSelectCard}
                />
            </div>
        </div>
    );
}

// ---- Top-level export ------------------------------------------------

export function CombosProfilePanel({
    profile,
    headline,
    strengths,
    risks,
    notable,
    highlights,
    cards,
    onSelectCard,
}) {
    if (!profile || !profile.count) return null;
    return (
        <div className="combosProfilePanel">
            <MarkersSection
                headline={headline}
                strengths={strengths}
                risks={risks}
                notable={notable}
            />
            <HighlightsSection highlights={highlights} onSelectCard={onSelectCard} />
            <FacetsSection profile={profile} cards={cards} onSelectCard={onSelectCard} />
        </div>
    );
}
